import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Divider, Stack, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import Modal from 'components/Common/Modal';
import { currencyCode } from 'appConfig';
import { formatAmount } from 'utils/helpers';
import { sendInvoice } from 'services/sendInvoice';
import { AppDispatch } from 'redux/store';
import Routes from 'constants/routes';
import ParishUsersAutocomplete from 'components/Common/autocomplete/ParishUsersAutocomplete';
import CardWrapper from 'components/Common/CardWrapper';
import Autocomplete from 'components/Common/Autocomplete';
import ParishesAutocomplete from 'components/Common/autocomplete/ParishesAutocomplete';
import BasketsAutocomplete from 'components/Common/autocomplete/BasketsAutocomplete';
import { Parish } from 'types/parishes';
import { UserInfo } from 'types/user';

export const fieldWidth = { xs: '90%', lg: '70%', xl: '50%' };
export const buttonWidth = { xs: '75%', lg: '55%', xl: '35%' };

const formSchema = yup.object({
    amount: yup
        .string()
        .required('Amount is required.')
        .test('amount', `Amount must be greater than $0.00 `, (value: any) => !(Number(value) < 0.01)),
    username: yup.string().when('sendTo', (sendTo, schema) => {
        if (sendTo[0] === 'parishioner') {
            return schema.required('Please select a Parishioner');
        }
        return schema;
    }),
    subject: yup.string(),
    currencyCode: yup.string().required('Currency Code is required'),
    sendTo: yup.string().required('Send To is required'),
    companySenderCode: yup.string().when('sendTo', (sendTo, schema) => {
        if (sendTo[0] === 'parish') {
            return schema.required('Please select a parish');
        }
        return schema;
    }),
    basketId: yup.string(),
});
const sendInvoiceOptions = [
    {
        label: 'Parish',
        value: 'parish',
    },
    {
        label: 'Parishioner',
        value: 'parishioner',
    },
];
export default function SendInvoiceForm() {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { parish, username } = state || {};
    const { name, companyCode, companyId } = parish || {};
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState<Parish | UserInfo | null>(null);
    const selectedParishId: string | undefined =
        selectedEntity && 'companyId' in selectedEntity ? String(selectedEntity.companyId) : undefined;
    const {
        watch,
        control,
        getValues,
        setValue,
        handleSubmit,
        trigger,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(formSchema),
        defaultValues: {
            username: username || '',
            amount: '',
            subject: '',
            currencyCode,
            sendTo: companyCode ? 'parish' : username ? 'parishioner' : '',
            companySenderCode: companyCode || '',
            basketId: '',
        },
    });

    const confirmSendInvoice = async () => {
        const { amount, companySenderCode, basketId, username, sendTo, ...formValues } = getValues();
        let normalizedValues: any = { ...formValues, amount: Number(amount) };
        if (sendTo === 'parish') {
            normalizedValues = { ...normalizedValues, companySenderCode };
        } else {
            normalizedValues = { ...normalizedValues, username };
        }

        setLoading(true);
        if (basketId) {
            normalizedValues.basketId = basketId;
        }
        const { payload } = await dispatch(sendInvoice(normalizedValues));
        setLoading(false);
        if (payload?.code === 200) {
            navigate(Routes.home.main);
        }
    };

    const handleFormSubmit = () => {
        setModalOpen(true);
    };

    return (
        <CardWrapper>
            <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                Send Invoice
            </Typography>
            <Divider />
            <Typography sx={{ mt: 2, mb: 4 }} variant="body1">
                Send invoices to other parishes, organizations, or parishioners. Select the recipient, the funding source, and the amount to
                send.
            </Typography>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <Stack width="100%" alignItems="center" gap={4}>
                    <Controller
                        name="sendTo"
                        control={control}
                        render={({ field: { value, onChange, ...rest } }) => (
                            <Autocomplete
                                {...rest}
                                value={watch('sendTo') || null}
                                disabled={name}
                                isOptionEqualToValue={(option) => option?.value === watch('sendTo')}
                                onChange={(_, item) => {
                                    const value = item?.value;
                                    if (value === 'parishioner' && !!watch('companySenderCode')) {
                                        setValue('companySenderCode', '');
                                    }
                                    setValue('sendTo', value);
                                    trigger('sendTo');
                                }}
                                getOptionKey={(option) => option?.value}
                                getOptionLabel={(option) => option?.label}
                                options={sendInvoiceOptions}
                                sx={{ width: fieldWidth }}
                                label="Send To"
                                placeholder="Please select a type to send money"
                                error={!!errors.sendTo}
                                helperText={(errors?.sendTo as any)?.message}
                            />
                        )}
                    />
                    {watch('sendTo') === 'parishioner' && (
                        <Controller
                            name="username"
                            control={control}
                            render={({ field: { onChange, value, ...rest } }) => (
                                <ParishUsersAutocomplete
                                    {...rest}
                                    user={value || null}
                                    setUser={(user) => {
                                        onChange(user?.username || '');
                                        trigger('username');
                                        setSelectedEntity(user);
                                    }}
                                    placeholder="Please select a parishioner"
                                    label="Parishioners"
                                    sx={{ width: fieldWidth }}
                                    error={!!errors.username}
                                    helperText={(errors?.username as any)?.message}
                                />
                            )}
                        />
                    )}
                    {watch('sendTo') === 'parish' && (
                        <Controller
                            name="companySenderCode"
                            control={control}
                            render={({ field: { value, onChange, ...rest } }) => (
                                <ParishesAutocomplete
                                    {...rest}
                                    sx={{ width: fieldWidth }}
                                    label="Select a parish"
                                    value={watch('companySenderCode') || null}
                                    matchKey="companyCode"
                                    defaultSearchText={name}
                                    disabled={name}
                                    onChange={(_, option) => {
                                        console.log('option', option);
                                        setValue('companySenderCode', option?.companyCode);
                                        setSelectedEntity(option);
                                        trigger('companySenderCode');
                                    }}
                                    error={!!errors.companySenderCode}
                                    helperText={(errors?.companySenderCode as any)?.message}
                                />
                            )}
                        />
                    )}

                    <Controller
                        name="basketId"
                        control={control}
                        render={({ field: { value, onChange, ...rest } }) => (
                            <BasketsAutocomplete
                                {...rest}
                                companyId={selectedParishId || companyId}
                                sx={{ width: fieldWidth }}
                                label="Select a basket"
                                onChange={(_, option) => {
                                    setValue('basketId', option?.id);
                                    trigger('basketId');
                                }}
                                error={!!errors.basketId}
                                helperText={(errors?.basketId as any)?.message}
                            />
                        )}
                    />

                    <Controller
                        name="amount"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                type="currency"
                                icon={null}
                                sx={{ width: fieldWidth }}
                                placeholder="$0.00"
                                label="Amount"
                                error={!!errors.amount}
                                helperText={(errors?.amount as any)?.message}
                            />
                        )}
                    />
                    <Controller
                        name="subject"
                        control={control}
                        render={({ field }) => (
                            <TextInput
                                {...field}
                                icon={null}
                                height={129}
                                sx={{ width: fieldWidth }}
                                placeholder="Type description here . . ."
                                label="Description"
                                minRows={4}
                                maxRows={4}
                                multiline
                            />
                        )}
                    />
                    <Button type="submit" sx={{ width: buttonWidth }} title="Send" />
                </Stack>
            </form>

            {/* Confirm Invoice Modal */}
            <Modal onClose={() => setModalOpen(false)} isOpen={modalOpen} modalTitle="Confirm Send Invoice">
                <>
                    <Typography sx={{ fontSize: { xs: 12, sm: 16 } }}>
                        Are you sure you want to send an invoice for <br />
                        <strong style={{ marginBottom: 2, fontSize: 20 }}>{formatAmount(watch('amount'), 'cents')}</strong>
                    </Typography>
                    <Typography style={{ whiteSpace: 'nowrap' }}>
                        <p style={{ color: '#1F2937', marginTop: 3, marginBottom: 3 }}>To</p>
                        <strong style={{ fontSize: 20 }}>{selectedEntity?.name || name}</strong>
                    </Typography>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                        <Button
                            disabled={loading}
                            onClick={() => setModalOpen(false)}
                            width={'45%'}
                            sx={{ fontSize: { xs: 14, sm: 18 } }}
                            title="Cancel"
                        />
                        <Button
                            loading={loading}
                            onClick={confirmSendInvoice}
                            width={'45%'}
                            sx={{ fontSize: { xs: 14, sm: 18 } }}
                            title="Confirm"
                        />
                    </div>
                </>
            </Modal>
        </CardWrapper>
    );
}
